import type { DateTime } from 'luxon'

import type { ExtendedBid } from '@/features/bidding/types/bid'
import { calculateCumulativeVolumes } from '@/features/bidding/utils/calculations/calculateCumulativeVolumes'
import { calculateIncrementalVolumes } from '@/features/bidding/utils/calculations/calculateIncrementalVolumes'
import { round } from '@/features/bidding/utils/calculations/convertToRoundedMw'
import { PriceLadderType } from '@/features/bidding/utils/getBidConfig'
import { getBidDisplayUnit } from '@/features/bidding/utils/getBidDisplayUnit'
import type { PriceQuantity } from '@/features/bidding/utils/price/getPtuPriceQuantitiesFromBids'
import { getPtuPriceQuantitiesFromExtendedBids } from '@/features/bidding/utils/price/getPtuPriceQuantitiesFromBids'

export type AggregatedBid = {
  ptus: AggregatedBidPtu[]
  ladderType: PriceLadderType
  unit: string
}

export type AggregatedBidPtu = {
  ptuStart: DateTime
  ptuEnd: DateTime
  available: number
  remaining: number
  priceQuantities: PriceQuantity[]
}

export const buildAggregatedBid = (bids: ExtendedBid[], priceLadderType: PriceLadderType): AggregatedBid => {
  if (bids.length === 0) return { ptus: [], unit: '', ladderType: priceLadderType }

  const ptusTimes = bids[0].ptus.map((ptu) => ptu.ptu)

  const ptus = ptusTimes.map((ptuTime) => {
    const priceQuantities = getPtuPriceQuantitiesFromExtendedBids(ptuTime.start, bids)
    const volumes =
      priceLadderType == PriceLadderType.INCREMENTAL
        ? calculateIncrementalVolumes(bids, ptuTime.start)
        : calculateCumulativeVolumes(bids, ptuTime.start)

    return {
      id: ptuTime.start.toMillis(),
      remaining: round(volumes.available - volumes.used),
      ptuStart: ptuTime.start,
      ptuEnd: ptuTime.end,
      available: volumes.available,
      priceQuantities,
    }
  })
  return {
    ptus,
    unit: getVolumeUnit(bids[0]),
    ladderType: priceLadderType,
  }
}

const getVolumeUnit = (bid: ExtendedBid): string => {
  if (!bid.ptus || bid.ptus.length === 0) {
    return ''
  }

  return getBidDisplayUnit(bid.ptus[0].offeredVolume.unit)
}

/**
 * This function is used to fix the prices to zero when the offered volume was also zero.
 * This is necessary because the PTU (Price Time Unit) may have been created with a zero offered volume,
 * which means that any associated prices should also be set to zero.
 * In case it is a new activation group, and there is no previous priceQuantities, create a new column of €0.00 and set it to zero.
 */
export function fixPricesToZeroWhenOfferedVolumeWasZero(bid: AggregatedBid): AggregatedBid {
  return {
    ...bid,
    ptus: bid.ptus.map((ptu) => {
      // If there is no priceQuantities, create a new one with price 0 and quantity 0
      if (ptu.priceQuantities.length === 0) {
        return {
          ...ptu,
          remaining: ptu.available === 0 ? 0 : ptu.remaining,
          priceQuantities: [{ price: 0, quantity: 0 }],
        }
      }

      // If the available volume is zero, we need to adjust the priceQuantities by setting their quantity to 0
      // Otherwise, we return the ptu as is
      if (ptu.available === 0) {
        return {
          ...ptu,
          remaining: 0,
          priceQuantities: ptu.priceQuantities.map((priceQuantity) => ({
            ...priceQuantity,
            quantity: 0,
          })),
        }
      }

      return ptu
    }),
  }
}
